import cloneDeep from 'lodash/cloneDeep';
import type { CompleteType } from '../util/general';
import { isObject } from '@mimir/primitive/type-guards/isObject';
import { getDefaultGroupId } from './tenantModel';
import { itemAccessControlLevels, type ItemAccessControl } from './itemAccessControlDto';

export const isInstanceOfItemAccessControlDto = (obj: unknown): obj is ItemAccessControl => {
  if (!isObject(obj)) return false;
  const possibleDto = obj as Partial<ItemAccessControl>;
  return (typeof possibleDto.level === 'string' && itemAccessControlLevels.includes(possibleDto.level)) || Array.isArray(possibleDto.groups);
};

export const toAccessControlDto = (access: ItemAccessControl): CompleteType<ItemAccessControl> => ({
  level: access.level,
  groups: access.groups,
});

/**
 * Returns default visibility access control for items. If user has a primary group that is different
 * from the 'default group' of a tenant, then by visibility is set to that primary group.
 * @param tenantId Tenant ID of user making the request
 * @param primaryGroupId Primary group ID of the user making the request
 * @param forExistingItem Whether this request is for an existing item. This is required for backwards
 * compatibility purposes since previously, the default visibility of items used to be everyone in the tenant.
 */
export const getDefaultVisibility = (tenantId: string, primaryGroupId: string, forExistingItem: boolean): ItemAccessControl => {
  const defaultGroupId = getDefaultGroupId(tenantId);
  if (forExistingItem || defaultGroupId === primaryGroupId) {
    return getDefaultAccessControl();
  }
  return {
    level: 'selected_groups',
    groups: [primaryGroupId],
  };
};

export const getDefaultAccessControl = (): ItemAccessControl => ({
  level: 'tenant',
  groups: [],
});

export const getSanitizedAccessControlForIngest = (access: ItemAccessControl): { sanitizedAccess: ItemAccessControl | undefined, errorMsg?: string } => {
  const sanitizedAccess = cloneDeep(access);

  if (access.level === 'tenant') {
    return { sanitizedAccess: toAccessControlDto(access) };
  }

  if (sanitizedAccess.level === 'owner') {
    return { sanitizedAccess: undefined, errorMsg: `Cannot set access control level to ${sanitizedAccess.level}` };
  }

  return { sanitizedAccess: toAccessControlDto(sanitizedAccess) };
};
